import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchWidgetAsset, type WidgetManifest } from '../../services/myToolsApi';
import '../../styles/Widgets.css';

export interface WidgetListingProps {
  toolId: string;
  manifest: WidgetManifest | null | undefined;
  /** Hide the README (screenshots + changelog only). */
  hideReadme?: boolean;
  className?: string;
}

/** Marketplace-style listing for a widget: screenshots, "what's new" and the bundle's README.
 *  Everything comes from the manifest + assets the author shipped inside the bundle. */
const WidgetListing: React.FC<WidgetListingProps> = ({ toolId, manifest, hideReadme = false, className }) => {
  const screenshots = manifest?.screenshots ?? [];
  const readmePath = hideReadme ? undefined : manifest?.readme;
  const [shotUrls, setShotUrls] = useState<Record<string, string>>({});
  const [readme, setReadme] = useState<string | null>(null);
  const [readmeError, setReadmeError] = useState<string | null>(null);
  const [enlarged, setEnlarged] = useState<string | null>(null);

  useEffect(() => {
    if (screenshots.length === 0) return;
    const controller = new AbortController();
    const created: string[] = [];
    screenshots.forEach(path => {
      fetchWidgetAsset(toolId, path, controller.signal)
        .then(asset => {
          if (controller.signal.aborted) return;
          if (asset.url.startsWith('blob:')) created.push(asset.url);
          setShotUrls(prev => ({ ...prev, [path]: asset.url }));
        })
        .catch(() => { /* a missing screenshot just doesn't show */ });
    });
    return () => {
      controller.abort();
      created.forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId, screenshots.join('|')]);

  useEffect(() => {
    if (!readmePath) { setReadme(null); return; }
    const controller = new AbortController();
    setReadme(null);
    setReadmeError(null);
    fetchWidgetAsset(toolId, readmePath, controller.signal)
      .then(asset => { if (!controller.signal.aborted) setReadme(asset.text ?? ''); })
      .catch(err => { if (!controller.signal.aborted) setReadmeError(err instanceof Error ? err.message : 'README unavailable'); });
    return () => controller.abort();
  }, [toolId, readmePath]);

  const hasAnything = screenshots.length > 0 || !!manifest?.changelog || !!readmePath;
  if (!hasAnything) return null;

  return (
    <div className={`wg-listing${className ? ` ${className}` : ''}`}>
      {screenshots.length > 0 && (
        <div className="wg-shots">
          {screenshots.map(path => (
            <button
              key={path}
              type="button"
              className={`wg-shots__item${enlarged === path ? ' wg-shots__item--large' : ''}`}
              onClick={() => setEnlarged(e => (e === path ? null : path))}
              title={enlarged === path ? 'Shrink' : 'Enlarge'}
            >
              {shotUrls[path]
                ? <img src={shotUrls[path]} alt={`${manifest?.name ?? 'Widget'} screenshot`} loading="lazy" />
                : <span className="wg-shots__placeholder">Loading…</span>}
            </button>
          ))}
        </div>
      )}
      {manifest?.changelog && (
        <div className="wg-listing__block">
          <span className="wg-perm-title">What's new in v{manifest.version}</span>
          <p className="wg-listing__changelog">{manifest.changelog}</p>
        </div>
      )}
      {readmePath && (
        <div className="wg-listing__block">
          <span className="wg-perm-title">About this widget</span>
          {readmeError && <p className="wg-listing__muted">{readmeError}</p>}
          {readme === null && !readmeError && <p className="wg-listing__muted">Loading README…</p>}
          {readme !== null && (
            <div className="wg-readme">
              <ReactMarkdown
                // Author-controlled Markdown: plain rendering only. No raw HTML (react-markdown
                // escapes it), no remote images (would leak the viewer's IP), links open safely.
                components={{
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
                  img: () => null,
                }}
              >
                {readme.slice(0, 32 * 1024)}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(WidgetListing);
